import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Paperclip, Send, X, ChevronDown } from 'lucide-react';
import { FILE_UPLOAD, ERROR_MESSAGES, CHAT_CONFIG } from '../constants';
import { useToast } from '../../../hooks';

interface FileAttachment {
  file: File;
  preview?: string;
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (files?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  // Agent selector props
  selectedAgentName?: string;
  onAgentClick?: () => void;
  showAgentSelector?: boolean;
  className?: string;
}

export interface ChatInputRef {
  focus: () => void;
  clear: () => void;
}

/**
 * ChatInput - Componente de input do chat
 *
 * Mantém a mesma funcionalidade do input atual, apenas com Tailwind CSS
 */
export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Digite sua mensagem...',
  selectedAgentName,
  onAgentClick,
  showAgentSelector = false,
  className = '',
}, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filePreviewListRef = useRef<HTMLDivElement>(null);

  const [attachedFiles, setAttachedFiles] = useState<FileAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const { showError, ToastContainer } = useToast();

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    clear: () => {
      onChange('');
      setAttachedFiles([]);
    },
  }));

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const lineHeight = parseInt(window.getComputedStyle(textareaRef.current).lineHeight) || 24;
      const maxHeight = window.innerWidth <= 768
        ? Math.min(150, lineHeight * CHAT_CONFIG.maxMessageLines)
        : lineHeight * CHAT_CONFIG.maxMessageLines;

      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxHeight)}px`;
      textareaRef.current.style.overflowY = textareaRef.current.scrollHeight > maxHeight ? 'auto' : 'hidden';
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [value]);

  // Horizontal scroll with mouse wheel for file previews
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (filePreviewListRef.current && filePreviewListRef.current.contains(e.target as Node)) {
        e.preventDefault();
        filePreviewListRef.current.scrollLeft += e.deltaY;
      }
    };

    const previewList = filePreviewListRef.current;
    if (previewList) {
      previewList.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (previewList) {
        previewList.removeEventListener('wheel', handleWheel);
      }
    };
  }, [attachedFiles]);

  // File validation
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (file.size > FILE_UPLOAD.maxFileSize) {
      return { valid: false, error: ERROR_MESSAGES.fileTooLarge(file.name, FILE_UPLOAD.maxFileSize) };
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !FILE_UPLOAD.allowedExtensions.includes(extension as typeof FILE_UPLOAD.allowedExtensions[number])) {
      return { valid: false, error: ERROR_MESSAGES.fileExtensionNotAllowed(file.name, FILE_UPLOAD.allowedExtensions) };
    }

    return { valid: true };
  };

  // Generate image preview
  const generateImagePreview = (file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      } else {
        resolve(undefined);
      }
    });
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    if (attachedFiles.length + fileArray.length > FILE_UPLOAD.maxFiles) {
      showError(ERROR_MESSAGES.maxFilesExceeded(FILE_UPLOAD.maxFiles));
      return;
    }

    const validFiles: FileAttachment[] = [];
    for (const file of fileArray) {
      const validation = validateFile(file);
      if (!validation.valid) {
        showError(validation.error || 'Arquivo inválido');
        continue;
      }
      const preview = await generateImagePreview(file);
      validFiles.push({ file, preview });
    }

    const currentTotalSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0);
    const newTotalSize = validFiles.reduce((sum, f) => sum + f.file.size, 0);

    if (currentTotalSize + newTotalSize > FILE_UPLOAD.maxTotalSize) {
      showError(ERROR_MESSAGES.totalSizeExceeded(FILE_UPLOAD.maxTotalSize, currentTotalSize));
      return;
    }

    setAttachedFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  // Handle file removal
  const handleFileRemove = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    if (attachedFiles.length + fileArray.length > FILE_UPLOAD.maxFiles) {
      showError(ERROR_MESSAGES.maxFilesExceeded(FILE_UPLOAD.maxFiles));
      return;
    }

    const validFiles: FileAttachment[] = [];
    for (const file of fileArray) {
      const validation = validateFile(file);
      if (!validation.valid) {
        showError(validation.error || 'Arquivo inválido');
        continue;
      }
      const preview = await generateImagePreview(file);
      validFiles.push({ file, preview });
    }

    const currentTotalSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0);
    const newTotalSize = validFiles.reduce((sum, f) => sum + f.file.size, 0);

    if (currentTotalSize + newTotalSize > FILE_UPLOAD.maxTotalSize) {
      showError(ERROR_MESSAGES.totalSizeExceeded(FILE_UPLOAD.maxTotalSize, currentTotalSize));
      return;
    }

    setAttachedFiles(prev => [...prev, ...validFiles]);
  };

  // Handle send
  const handleSend = () => {
    if (!value.trim() && attachedFiles.length === 0) return;
    onSend(attachedFiles.map(f => f.file));
    setAttachedFiles([]);
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMobile = window.innerWidth <= 768;
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Get file extension
  const getFileExtension = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return extension ? extension.toUpperCase() : 'FILE';
  };

  const totalSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0);

  return (
    <>
      <ToastContainer />
      <div className={`bg-[#242424] sticky bottom-0 w-full py-2.5 px-0 z-10 ${className}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        className="max-w-[800px] mx-auto px-4 relative w-full box-border"
      >
        {/* Input Wrapper */}
        <div
          className="
            relative bg-[#1a1a1a] rounded-2xl
            border-2 border-[#333333]
            transition-all duration-200
            shadow-[0_2px_8px_rgba(0,0,0,0.1)]
            focus-within:border-[#F4CC04] focus-within:shadow-[0_0_0_3px_rgba(244,204,4,0.1)]
          "
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag Overlay */}
          {isDragging && (
            <div className="
              absolute inset-0.5
              bg-[#F4CC04]/10 border-3 border-dashed border-[#F4CC04]
              rounded-[14px] flex items-center justify-center z-50
              backdrop-blur-sm pointer-events-none
            ">
              <div className="flex flex-col items-center gap-3 text-[#F4CC04]">
                <Paperclip size={48} />
                <p className="text-lg font-semibold">Solte os arquivos aqui</p>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept={FILE_UPLOAD.acceptAttribute}
          />

          {/* Input Row */}
          <div className="flex items-center gap-2 p-3 min-h-[56px]">
            <div
              className="flex-1 flex items-center cursor-text"
              onClick={() => textareaRef.current?.focus()}
            >
              <textarea
                ref={textareaRef}
                className="
                  flex-1 bg-transparent border-none
                  text-white text-base leading-6
                  min-h-[24px] max-h-[240px]
                  p-0 resize-none outline-none
                  font-inherit overflow-y-auto w-full
                  placeholder:text-[#888888]
                "
                value={value}
                onChange={(e) => {
                  const lines = e.target.value.split('\n');
                  if (lines.length > CHAT_CONFIG.maxMessageLines) {
                    onChange(lines.slice(-CHAT_CONFIG.maxMessageLines).join('\n'));
                  } else {
                    onChange(e.target.value);
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
              />
            </div>

            <button
              type="submit"
              className={`
                bg-[#F4CC04] border-none p-2
                text-black cursor-pointer rounded-lg
                flex items-center justify-center
                transition-all duration-200
                min-w-[40px] h-10 font-semibold
                hover:bg-[#ffd700] hover:scale-105 hover:shadow-[0_4px_12px_rgba(244,204,4,0.3)]
                disabled:bg-[#333333] disabled:text-[#888888] disabled:cursor-not-allowed disabled:opacity-50
              `}
              disabled={disabled || (!value.trim() && attachedFiles.length === 0)}
              aria-label="Enviar mensagem"
            >
              {disabled ? (
                <div className="w-5 h-5 border-2 border-[#F4CC04]/10 border-t-[#F4CC04] rounded-full animate-spin" />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>

          {/* Actions Row */}
          <div className="px-4 pb-3 flex items-center gap-2">
            <button
              type="button"
              className="
                bg-transparent border-none text-[#888888]
                cursor-pointer flex items-center gap-1.5
                p-2 text-sm transition-all duration-200 rounded-md
                hover:text-[#F4CC04] hover:bg-[#F4CC04]/10 hover:scale-105
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              aria-label="Anexar arquivo"
            >
              <Paperclip size={20} />
            </button>

            {/* Agent Selector */}
            {showAgentSelector && (
              <div className="flex-1 relative">
                <button
                  type="button"
                  className="
                    flex items-center justify-between w-full
                    bg-white/[0.03] border border-white/10
                    text-white cursor-pointer
                    gap-2 px-3 py-2 text-sm
                    transition-all duration-200 rounded-lg
                    hover:border-[#F4CC04] hover:bg-[#F4CC04]/5
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                  onClick={onAgentClick}
                  disabled={disabled}
                >
                  <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap font-medium" title={selectedAgentName}>
                    {selectedAgentName && selectedAgentName.length > 40
                      ? `${selectedAgentName.substring(0, 40)}...`
                      : (selectedAgentName || 'Selecione um agente')}
                  </span>
                  <ChevronDown size={16} className="flex-shrink-0" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* File Progress Bar */}
        {attachedFiles.length > 0 && (
          <div className="px-4 mt-3 mb-3 animate-in slide-in-from-top-2 duration-300">
            <div className="flex justify-between items-center mb-2 text-sm">
              <span className="text-white font-medium">
                {attachedFiles.length} arquivo{attachedFiles.length !== 1 ? 's' : ''} anexado{attachedFiles.length !== 1 ? 's' : ''}
              </span>
              <span className="text-[#F4CC04] font-semibold font-mono">
                {(totalSize / 1024 / 1024).toFixed(2)} MB / {(FILE_UPLOAD.maxTotalSize / 1024 / 1024).toFixed(0)} MB
              </span>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden shadow-[inset_0_1px_3px_rgba(0,0,0,0.3)]">
              <div
                className={`
                  h-full rounded-full transition-all duration-300
                  ${totalSize / FILE_UPLOAD.maxTotalSize > 0.9
                    ? 'bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 animate-pulse'
                    : 'bg-gradient-to-r from-[#F4CC04] via-[#ffd700] to-[#F4CC04]'
                  }
                  bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]
                  shadow-[0_0_10px_rgba(244,204,4,0.5)]
                `}
                style={{ width: `${Math.min((totalSize / FILE_UPLOAD.maxTotalSize) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* File Preview List */}
        {attachedFiles.length > 0 && (
          <div
            ref={filePreviewListRef}
            className="
              px-4 mb-3 flex flex-row gap-2.5
              overflow-x-auto overflow-y-hidden
              scroll-smooth
              scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20
            "
          >
            {attachedFiles.map((attachment, index) => {
              const isImage = attachment.file.type.startsWith('image/');
              return (
                <div
                  key={index}
                  className="
                    relative flex flex-col items-center
                    bg-white/5 border border-white/10 rounded-xl
                    p-2.5 min-w-[110px] max-w-[110px]
                    transition-all duration-300
                    hover:bg-white/[0.08] hover:border-[#F4CC04]/40 hover:shadow-[0_2px_8px_rgba(244,204,4,0.2)]
                    animate-in slide-in-from-bottom-2 zoom-in-95
                  "
                >
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleFileRemove(index)}
                    className="
                      absolute -top-0.5 -right-0.5
                      bg-red-500 border-2 border-black
                      text-white cursor-pointer
                      w-6 h-6 rounded-full
                      flex items-center justify-center
                      transition-all duration-200 z-10
                      opacity-0 group-hover:opacity-100
                      hover:bg-red-600 hover:shadow-[0_2px_8px_rgba(255,68,68,0.4)]
                    "
                    style={{ opacity: 1 }}
                    aria-label="Remover arquivo"
                  >
                    <X size={14} />
                  </button>

                  {/* Thumbnail */}
                  <div className="
                    w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden
                    bg-gradient-to-br from-[#F4CC04]/10 to-[#F4CC04]/5
                    flex items-center justify-center
                    border border-[#F4CC04]/20 mb-2
                  ">
                    {isImage && attachment.preview ? (
                      <img
                        src={attachment.preview}
                        alt={attachment.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-bold text-[#F4CC04] uppercase tracking-wide">
                        {getFileExtension(attachment.file.name)}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex flex-col gap-0.5 w-full text-center">
                    <span
                      className="text-white text-[11px] font-medium overflow-hidden text-ellipsis whitespace-nowrap w-full"
                      title={attachment.file.name}
                    >
                      {attachment.file.name}
                    </span>
                    <span className="text-[#888888] text-[10px] font-mono">
                      {formatFileSize(attachment.file.size)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </form>
    </div>
    </>
  );
});

ChatInput.displayName = 'ChatInput';
